import { Grid } from '@mui/material';
import React, { ComponentProps, MouseEventHandler, useState } from 'react';
import {
  asPercentage,
  asPercentageRounded,
  fixedNumberWithCommas,
  numberWithCommas,
  removeLineBreaks,
} from 'spw-shared/utils';
import { useDisableBulkEditButton } from 'spw-shared/hooks/use-disable-bulk-edit-button';

import {
  ChildName,
  BorderedRightChildName,
  AsteriskContainer,
} from '../macro-single-page-summary/styles';
import WorksheetRow from '../../../../models/WorksheetRow';
import WorksheetStorageSpaceRow from '../../../../models/WorksheetStorageSpaceRow';
import BulkEditDialog from '../program-summary-tabs/bulk-edit-dialog';
import { AvgSeatsBulkEditButton } from './style';

const BULK_EDIT_DIALOG_SUBJECT = 'Seats';

export const CategorySeatTypeMixChildRow = ({
  row,
  showDecimals,
  shouldShowClientFacingName,
  isIndividualWorkspacesCard = false,
}: {
  row: WorksheetRow;
  showDecimals: boolean;
  shouldShowClientFacingName: boolean;
  isIndividualWorkspacesCard?: boolean;
}) => {
  const [bulkEditDialogProps, setBulkEditDialogProps] = useState<
    Omit<ComponentProps<typeof BulkEditDialog>, 'clientFacingNamesAreVisible'>
  >();
  const disableBulkEditButton = useDisableBulkEditButton({
    row,
    subject: BULK_EDIT_DIALOG_SUBJECT,
  });

  const handleBulkEditDialogClose = () => {
    setBulkEditDialogProps(undefined);
  };

  const isStorageRow = row.isStorageSupportSpace;
  const seats = row.quantity * row.seatCap;
  const percent = row.ratio / 100;
  let percentValue = '0%';

  if (!Number.isNaN(percent)) {
    percentValue = showDecimals
      ? asPercentage(row.ratio / 100)
      : asPercentageRounded(row.ratio / 100, false);
  }

  let qtyVal = showDecimals ? '0.00' : 0;
  if (row.quantity) {
    qtyVal = showDecimals
      ? fixedNumberWithCommas(row.quantity)
      : numberWithCommas(Math.round(row.quantity));
  }

  const ratio = row.projectSpaceTypeSubcategory?.includeInTotalWorkSeats
    ? percentValue
    : `1: ${numberWithCommas(row.ratio, showDecimals, true)}`;

  const testingPrefixForRow = `programming_ps_seat_type_mix_row_child_${row?.spaceType?.id}`;

  const handleAvgSeatsBulkEditButtonClick = ((e) => {
    setBulkEditDialogProps({
      projectSpaceType: row.projectSpaceType,
      subject: BULK_EDIT_DIALOG_SUBJECT,
      anchorEl: e.currentTarget,
      onClose: handleBulkEditDialogClose,
    });
  }) as MouseEventHandler;

  return (
    <Grid container key={`row-${row.id}`}>
      <Grid item xs={3}>
        <ChildName
          className="no-margin"
          data-testid={`${testingPrefixForRow}_spaceTypeName`}
        >
          <pre>
            {removeLineBreaks(
              shouldShowClientFacingName &&
                row.projectSpaceType.clientFacingName
                ? row.projectSpaceType.clientFacingName
                : row.spaceType.name
            )}
          </pre>
        </ChildName>
      </Grid>
      <Grid item xs={2}>
        {isStorageRow ? (
          <BorderedRightChildName
            noBorder
            className="value"
            data-testid={`${testingPrefixForRow}_quantity`}
          >
            {numberWithCommas(
              (row as WorksheetStorageSpaceRow).lockersOrDrawersQuantity,
              showDecimals,
              true
            )}
            <AsteriskContainer>*</AsteriskContainer>
          </BorderedRightChildName>
        ) : (
          <BorderedRightChildName
            data-testid={`${testingPrefixForRow}_quantity`}
            noBorder={isIndividualWorkspacesCard}
          >
            {qtyVal}
          </BorderedRightChildName>
        )}
      </Grid>
      <Grid item xs={7}>
        <Grid container>
          <Grid
            item
            xs={isStorageRow ? 4 : 6}
            style={{
              textAlign: 'right',
            }}
          >
            {isStorageRow ? (
              <ChildName data-testid={`${testingPrefixForRow}_ratio`}>
                1: {numberWithCommas(row.ratio, showDecimals, true)}
              </ChildName>
            ) : (
              <>
                <AvgSeatsBulkEditButton
                  $disableHoverState={disableBulkEditButton}
                  className={bulkEditDialogProps ? 'selected' : ''}
                  onClick={
                    !disableBulkEditButton
                      ? handleAvgSeatsBulkEditButtonClick
                      : undefined
                  }
                  padding={0}
                >
                  <ChildName
                    className="value"
                    data-testid={`${testingPrefixForRow}_seats`}
                  >
                    {numberWithCommas(seats, showDecimals, true)}
                    {!row.isIncludedInTotalWorkSeats && (
                      <AsteriskContainer>*</AsteriskContainer>
                    )}
                  </ChildName>
                </AvgSeatsBulkEditButton>
                {bulkEditDialogProps && (
                  <BulkEditDialog
                    {...bulkEditDialogProps}
                    clientFacingNamesAreVisible={shouldShowClientFacingName}
                  />
                )}
              </>
            )}
          </Grid>
          {isStorageRow && (
            <Grid
              item
              xs={5}
              style={{
                textAlign: 'right',
                paddingRight: 20,
              }}
            >
              <ChildName
                className="value"
                data-testid={`${testingPrefixForRow}_unitCount`}
              >
                {numberWithCommas(
                  (row as WorksheetStorageSpaceRow).quantity,
                  showDecimals,
                  true
                )}
                <AsteriskContainer>*</AsteriskContainer>
              </ChildName>
            </Grid>
          )}
          <Grid
            item
            xs={isStorageRow ? 3 : 6}
            style={{
              textAlign: 'right',
              paddingRight: '0',
            }}
          >
            {isStorageRow ? (
              <ChildName data-testid={`${testingPrefixForRow}_unitRatio`}>
                {`1: ${numberWithCommas(
                  (row as WorksheetStorageSpaceRow).storageUnitsRatio,
                  showDecimals,
                  true
                )}`}
              </ChildName>
            ) : (
              <ChildName data-testid={`${testingPrefixForRow}_seatMix`}>
                {ratio}
              </ChildName>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
